/**
 * English message catalog — the source of every message key.
 *
 * Nested JSON-shaped object, the layout react-i18next reads as a resource
 * bundle, so moving to it is a copy (see ../README.md). Rules:
 *
 * - Interpolation is `{{name}}`. Never type a number, amount or date into a
 *   string; pass it as a parameter and the layer formats it for the locale.
 * - `{{product}}` is the product name (PRODUCT_NAME); it is always supplied.
 * - Plurals use i18next's suffixes: `key_one`, `key_other` here; Arabic adds
 *   `_zero`, `_two`, `_few`, `_many`. Pass `{ count }` to t() with the base key.
 * - Error copy follows docs/help/errors.md; terms follow docs/help/glossary.md.
 * - The launch screens converted in C7-REST keep their messages in
 *   ./screens.en.ts, spread in at the end; UI-HONEST's (disabled controls
 *   and their reasons, confirmations, sessions, two-step sign-in, API
 *   clients) are in ./controls.en.ts, spread in after them.
 */
import { controlsEn } from "./controls.en";
import { screensEn } from "./screens.en";

export const en = {
  common: {
    tryAgain: "Try again",
    details: "Details",
    chooseCountry: "Choose a country",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    create: "Create",
    select: "Select…",
    /** The interface's languages, named in the language being read. */
    languageNames: {
      en: "English",
      ar: "Arabic",
    },
  },
  /**
   * The page's own <title> and description (index.html carries the product
   * name only; applyDocumentLocale sets these in the active language).
   */
  meta: {
    title: "{{product}} ERP",
    description:
      "{{product}} — production-grade, multi-tenant, global ERP SaaS. Configure, don't code. Govern, don't obstruct. Explain, don't hide.",
  },
  /** Shared primitives in components/ui.tsx. */
  ui: {
    breadcrumb: "Breadcrumb",
    howToFix: "How to fix:",
    requiredNote: "Fields marked with an asterisk are required.",
  },
  /** ErrorBoundary: a screen that crashed while rendering. */
  crash: {
    title: "Something went wrong",
    body: "Try again or contact support.",
  },
  /** ResourceListPage, the generic list screen. */
  resource: {
    loadFailed: "Could not load {{title}}",
  },
  /**
   * Kinds of record, by the code the API sends (ApprovalRequest.entity_type,
   * the search groups' entityType). Terms from docs/help/glossary.md.
   */
  records: {
    types: {
      PurchaseOrder: "Purchase order",
      SalesOrder: "Sales order",
      SalesInvoice: "Sales invoice",
      JournalEntry: "Journal entry",
      Customer: "Customer",
      Supplier: "Supplier",
      Product: "Product",
      SupplierInvoice: "Supplier invoice",
      GoodsReceipt: "Goods receipt",
      Payment: "Payment",
      InventoryMovement: "Stock movement",
      ProductionOrder: "Production order",
      SalesOrderDelivery: "Sales order delivery",
    },
  },
  /**
   * Status words by record type and stored value: the glossary's status table
   * ("never show the raw value"). Arabic agrees with the record's gender.
   */
  status: {
    JournalEntry: {
      Draft: "Draft",
      Submitted: "Awaiting approval",
      Approved: "Approved",
      Posted: "Posted",
      Reversed: "Reversed",
      Voided: "Voided",
    },
    SalesOrder: {
      Draft: "Draft",
      Confirmed: "Confirmed",
      Approved: "Approved",
      Released: "Released",
      Fulfilled: "Fulfilled",
      Delivered: "Delivered",
      Invoiced: "Invoiced",
      Completed: "Completed",
      Cancelled: "Cancelled",
    },
    SalesInvoice: {
      Draft: "Draft",
      Approved: "Approved",
      Posted: "Posted",
      Sent: "Sent",
      Paid: "Paid",
      Overdue: "Overdue",
      WrittenOff: "Written off",
    },
    PurchaseOrder: {
      Draft: "Draft",
      Approved: "Approved",
      Sent: "Sent to supplier",
      Confirmed: "Confirmed",
      PartiallyReceived: "Partly received",
      Received: "Received",
      Closed: "Closed",
      Cancelled: "Cancelled",
    },
    Customer: {
      Active: "Active",
      Blocked: "Blocked",
      Dormant: "Dormant",
    },
    Supplier: {
      Active: "Active",
      Blocked: "Blocked",
      Dormant: "Dormant",
    },
    Product: {
      Draft: "Draft",
      Active: "Active",
      Obsolete: "Obsolete",
    },
    // C7-REST: the statuses of the launch screens it converts, by the record
    // type the API stores them on (the database enums). The glossary's status
    // table gives Workspace, AccountingPeriod and InventoryMovement.
    Workspace: {
      Provisioning: "Being set up",
      Active: "Active",
      Suspended: "Suspended",
      LegalHold: "On legal hold",
      Closed: "Closed",
    },
    AccountingPeriod: {
      Open: "Open",
      Closing: "Closing",
      Closed: "Closed",
    },
    InventoryMovement: {
      Draft: "Draft",
      Posted: "Posted",
    },
    LegalEntity: {
      Provisional: "Provisional",
      Active: "Active",
      Inactive: "Inactive",
    },
    Company: {
      Draft: "Draft",
      Active: "Active",
      Closed: "Closed",
    },
    Branch: {
      Active: "Active",
      Inactive: "Inactive",
    },
    Location: {
      Active: "Active",
      Inactive: "Inactive",
    },
    BankStatement: {
      Uploaded: "Uploaded",
      Matched: "Matched",
      Posted: "Posted",
    },
    FixedAsset: {
      Draft: "Draft",
      Active: "Active",
      Depreciated: "Depreciated",
      Disposed: "Disposed",
      Impairment: "Impairment",
    },
    TaxReturn: {
      Draft: "Draft",
      Filed: "Filed",
      Overdue: "Overdue",
      Paid: "Paid",
    },
    Budget: {
      Draft: "Draft",
      Active: "Active",
      Superseded: "Superseded",
    },
    ConsolidationGroup: {
      NotRun: "Not run",
      Completed: "Completed",
      Failed: "Failed",
    },
    IntercompanyItem: {
      Open: "Open",
      Matched: "Matched",
      Settled: "Settled",
    },
    DeliveryNote: {
      Draft: "Draft",
      Picking: "Picking",
      Packed: "Packed",
      Shipped: "Shipped",
      Delivered: "Delivered",
      Partial: "Partial",
      Returned: "Returned",
    },
    SalesReturn: {
      Requested: "Requested",
      Approved: "Approved",
      Received: "Received",
      Rejected: "Rejected",
      Credited: "Credited",
      Replaced: "Replaced",
    },
    Discount: {
      Active: "Active",
      Draft: "Draft",
      Superseded: "Superseded",
    },
    CreditLimitRequest: {
      Pending: "Pending",
      Approved: "Approved",
      Declined: "Declined",
    },
    Commission: {
      Accrued: "Accrued",
      Approved: "Approved",
      Paid: "Paid",
    },
    PurchaseRequest: {
      Draft: "Draft",
      Submitted: "Submitted",
      Approved: "Approved",
      Rejected: "Rejected",
      Converted: "Converted",
      Cancelled: "Cancelled",
    },
    Rfq: {
      Draft: "Draft",
      Sent: "Sent",
      QuotesReceived: "Quotes received",
      Awarded: "Awarded",
      Cancelled: "Cancelled",
    },
    ProcurementContract: {
      Active: "Active",
      Expiring: "Expiring",
      Expired: "Expired",
      Draft: "Draft",
      Cancelled: "Cancelled",
    },
    StockTransfer: {
      Draft: "Draft",
      InTransit: "In transit",
      Received: "Received",
      Posted: "Posted",
      Cancelled: "Cancelled",
    },
    StockAdjustment: {
      Draft: "Draft",
      PendingApproval: "Awaiting approval",
      Approved: "Approved",
      Posted: "Posted",
      Rejected: "Rejected",
    },
    CycleCount: {
      Draft: "Draft",
      InProgress: "In progress",
      Reviewed: "Reviewed",
      Approved: "Approved",
      Posted: "Posted",
    },
    ApiClient: {
      Active: "Active",
      Revoked: "Revoked",
    },
    OutboxMessage: {
      Pending: "Pending",
      Sending: "Sending",
      Sent: "Sent",
      DeadLetter: "Dead letter",
    },
    TenantUser: {
      Invited: "Invited",
      Active: "Active",
      Suspended: "Suspended",
      Removed: "Removed",
    },
    Role: {
      Draft: "Draft",
      Active: "Active",
      Superseded: "Superseded",
    },
    EntityDefinition: {
      Draft: "Draft",
      Validated: "Validated",
      Simulated: "Simulated",
      Approved: "Approved",
      Active: "Active",
      Superseded: "Superseded",
    },
    ViewDefinition: {
      Draft: "Draft",
      Active: "Active",
      Superseded: "Superseded",
    },
    LayoutDefinition: {
      Draft: "Draft",
      Active: "Active",
    },
    ApprovalMatrix: {
      Draft: "Draft",
      Active: "Active",
      Superseded: "Superseded",
    },
    Connector: {
      Connected: "Connected",
      Error: "Error",
      NotConfigured: "Not configured",
      Disabled: "Disabled",
    },
    ImportJob: {
      Uploaded: "Uploaded",
      Validating: "Validating",
      Previewing: "Previewing",
      DryRun: "Dry run",
      Completed: "Completed",
      Failed: "Failed",
      RolledBack: "Rolled back",
    },
    Subscription: {
      Active: "Active",
      PastDue: "Past due",
      Cancelled: "Cancelled",
      Trial: "Trial",
    },
    SubscriptionInvoice: {
      Paid: "Paid",
      Open: "Open",
      Overdue: "Overdue",
    },
  },
  /** Amounts and currencies (docs/design/currency.md, CUR-2). */
  money: {
    /** Beneath a total: the same figure in other currencies, never added in. */
    othersNotConverted: "Also {{amounts}}, not converted",
    currency: "Currency",
    chooseCurrency: "Choose a currency",
    currencyHint:
      "The currency of this record's amounts. Starts as the workspace currency.",
    allCurrencies: "All currencies",
    fromCurrency: "From",
    toCurrency: "To",
  },
  /** A report that is one set of books (422 LEGAL_ENTITY_REQUIRED). */
  books: {
    legalEntity: "Legal entity",
    chooseLegalEntity: "Choose a legal entity",
    entityOption: "{{name}} ({{currency}})",
    legalEntityRequired:
      "This workspace keeps its books in more than one currency, so a report shows one legal entity's books at a time. Choose the legal entity.",
  },
  shell: {
    primaryNav: "Primary",
    toggleNav: "Toggle navigation",
    commands: "Commands",
    themeToLight: "Switch to light theme",
    themeToDark: "Switch to dark theme",
    noWorkspace: "No workspace",
    cell: "Cell: {{cell}}",
    palette: {
      label: "Command palette",
      placeholder: "Commands, navigation, records…",
      noResults: "No results for “{{query}}”.",
    },
  },
  session: {
    ended: "Your session ended. Please sign in again.",
    workspaceCreated:
      "Your workspace has been created. Sign in again to open it.",
    workspaceCreatedMultiple:
      "Your workspace has been created. Sign in again. You belong to more than one workspace, and {{product}} opens the first one in alphabetical order; choosing which one opens is not available yet.",
    /**
     * W2.8b (D1002): the refresh was paused because two-step sign-in became
     * required here and is not set up; the session was ended.
     */
    mfaRequired:
      "Two-step sign-in is now required for you in this workspace. Sign in again to set it up; the workspace opens as soon as it is confirmed.",
    signOut: "Sign out",
    signingOut: "Signing out…",
    checkAgain: "Check again",
    checking: "Checking…",
    stillUnavailable: "It is still not available. Try again in a moment.",
    workspaceBlock: {
      suspended: {
        title: "This workspace is suspended",
        body: "It cannot be used for now. Contact the owner of your workspace, or {{product}} support.",
      },
      provisioning: {
        title: "Your workspace is still being set up",
        body: "This usually takes a moment. Wait a little, then choose Check again. You do not need to sign in again.",
      },
      legalHold: {
        title: "This workspace is on legal hold",
        body: "It cannot be used for now. Contact the owner of your workspace, or {{product}} support.",
      },
      closed: {
        title: "This workspace has been closed",
        body: "Contact the owner of your workspace, or {{product}} support.",
      },
      notFound: {
        title: "This workspace could not be found",
        body: "Sign out, then sign in again.",
      },
      notActive: {
        title: "This workspace is not available",
        body: "It is not active right now. Contact the owner of your workspace, or {{product}} support.",
      },
    },
  },
  auth: {
    login: {
      heading: "Sign in to {{product}}",
      title: "Sign in",
      subtitle: "Use your work email to continue.",
      email: "Work email",
      password: "Password",
      submit: "Sign in",
      submitting: "Signing in…",
      createWorkspace: "Create workspace",
    },
    mfa: {
      heading: "Two-step verification",
      title: "Enter your code",
      subtitle:
        "Open your authenticator app and enter the {{digits}}-digit code it is showing. If you cannot reach that device, enter one of your recovery codes instead.",
      codeLabel: "Verification code",
      codePlaceholder: "{{example}} or {{recoveryExample}}",
      codeHelp:
        "Codes change every {{seconds}} seconds. A recovery code works once.",
      submit: "Verify",
      submitting: "Verifying…",
      notAccepted:
        "That code was not accepted. Try the next one your app shows.",
      lostDevice:
        "Lost your device and your recovery codes? An administrator has to reset two-step sign-in for you. It cannot be done from this screen, so that nobody else can do it either.",
      backToSignIn: "Sign in",
    },
    /** SignUpPage. */
    signup: {
      title: "Create your {{product}} account",
      subtitle_one: "Start your workspace — it takes about {{count}} minute.",
      subtitle_other:
        "Start your workspace — it takes about {{count}} minutes.",
      name: "Full name",
      email: "Work email",
      emailHint: "We'll verify this email before provisioning.",
      password: "Password",
      passwordHint_one: "Minimum {{count}} character.",
      passwordHint_other: "Minimum {{count}} characters.",
      submit: "Sign up",
      submitting: "Creating account…",
      haveAccount: "Already have an account?",
      signIn: "Sign in",
      /** W2.5, W2.8a: too many sign-ups for this address or from this network. */
      limited:
        "There have been several sign-ups or code requests for this address, or from your network, in a short time. Wait up to an hour, then try again.",
    },
    /** VerifyEmailPage. */
    verify: {
      title: "Verify your email",
      subtitle: "Enter the {{digits}}-digit code we sent to your inbox.",
      done: "Email verified. Sign in to create your workspace.",
      code: "Verification code",
      submit: "Verify email",
      submitting: "Verifying…",
      /** W2.5: asking for a new code. */
      resendTitle: "Didn't get the code?",
      resendHint:
        "Enter the email address you signed up with and we will send a new code. Codes sent before it stop working.",
      resendEmail: "Email address",
      resendSubmit: "Send a new code",
      resendSending: "Sending a new code…",
      resendRetry: "Try sending a new code again",
      resendSentBadge: "Sent",
      resendSent:
        "If {{email}} is waiting to be verified, a new code is on its way and the codes sent before it no longer work. If that address already has a verified account, we have sent it a reminder to sign in instead.",
      resendLimited:
        "You have asked for several codes in a short time. Wait up to an hour, then ask again. The most recent code you received still works until it expires.",
    },
    /** WorkspaceCreationPage (D25). */
    workspace: {
      title: "Create your workspace",
      subtitle:
        "Choose where your company operates. {{product}} installs that country's pack and keeps the currency, time zone and language you choose.",
      name: "Company name",
      code: "Workspace code",
      codeHint: "Lowercase letters, digits and hyphens. Used in links.",
      country: "Country",
      countryHint: "This country's pack is installed in your workspace.",
      countriesLoading: "Loading the countries you can choose…",
      countriesFailed: "The list of countries could not be loaded.",
      noCountries:
        "No country can be chosen yet, so a workspace cannot be created. Contact support.",
      chooseCountry: "Choose a country",
      currency: "Currency",
      currencyHint: "The currency your books are kept in.",
      chooseCurrency: "Choose a currency",
      timezone: "Time zone",
      timezoneHint:
        "Dates and times in this workspace use this zone, unless a person chooses their own.",
      chooseTimezone: "Choose a time zone",
      language: "Language",
      languageHint:
        "The workspace's language and region. Each person still chooses the language of their own screens.",
      chooseLanguage: "Choose a language",
      submit: "Create workspace",
      submitting: "Creating your workspace…",
      errors: {
        name: "Enter the company name, {{min}} to {{max}} characters.",
        code: "Enter {{min}} to {{max}} lowercase letters, digits or hyphens.",
        codeTaken:
          "Another workspace already uses this code. Choose a different one.",
        country: "Choose the country your company operates in.",
        currency: "Choose a currency.",
        timezone: "Choose a time zone.",
        language: "Choose a language.",
      },
    },
  },
  errors: {
    AUTHENTICATION_FAILED: {
      credentials:
        "The e-mail or password is wrong, or the account cannot sign in yet. Check the e-mail and password. If you have just signed up, verify your e-mail first. After {{attempts}} wrong attempts the account is locked for {{minutes}} minutes; wait, then try again. {{product}} has no password reset yet; contact support if you have forgotten it.",
      noFactor:
        "Your account must use two-step sign-in, but no authenticator app is set up for it. Contact support to reset two-step sign-in on your account.",
      codeInvalid:
        "The code does not match the authenticator app or any unused recovery code. Enter the code your authenticator app shows now, or one of your recovery codes that you have not used yet.",
      codeUsed:
        "Each code works once. Wait for your authenticator app to show the next code, then enter it.",
      attemptExpired:
        "Too long passed between your password and your code. The second step must be finished within {{minutes}} minutes. Go back to the sign-in page and start again.",
    },
    /**
     * VALIDATION_FAILED on sign-up and e-mail verification, told apart by the
     * API's detail (lib/i18n/errors.ts). docs/help/errors.md, cause then fix.
     */
    VALIDATION_FAILED: {
      email:
        "The e-mail address is empty or not complete. Enter your full e-mail address, such as name@company.com.",
      passwordTooShort:
        "The password is shorter than {{min}} characters. Use at least {{min}} characters. A short sentence is easy to remember.",
      passwordTooLong:
        "The password is longer than {{max}} characters. Use a shorter password.",
      passwordSpace:
        "The password begins or ends with a space. Remove the space at the start or end.",
      codeFormat:
        "The code is not {{digits}} digits. Enter the {{digits}}-digit code from the verification e-mail.",
      codeInvalid:
        "The code is wrong, was already used, or is more than {{minutes}} minutes old. Check the code and enter it again. {{product}} cannot send a new code yet, and signing up again does not send one; if it has expired, contact support.",
    },
    RATE_LIMITED:
      "Too many sign-in attempts, or too many two-step codes, came from your network: more than {{limit}} in {{minutes}} minutes. Wait a few minutes, then try again.",
    NETWORK_ERROR:
      "{{product}} could not be reached. Your connection may be down, or {{product}} may be restarting. Check your connection, wait a moment, then try again.",
    UNKNOWN:
      "{{product}} answered with an error but did not say what kind. Try again. If it happens again, contact support with the reference.",
    fallback:
      "This action did not finish. Try again. If it happens again, contact support.",
    /**
     * The 15 session, token and guard codes S2-05f listed (libs/contracts
     * ERROR_CODES); the words are docs/help/errors.md's for people in the
     * app, cause then fix, its "Atlas" as {{product}} and its time limits as
     * parameters (lib/i18n/errors.ts SESSION_LIMITS).
     */
    TOKEN_EXPIRED:
      "Your session needed renewing, and {{product}} could not renew it. Reload the page. If {{product}} then asks you to sign in, sign in again.",
    TOKEN_INVALID:
      "{{product}} could not recognise your session, for example because {{product}} has changed its security keys. Reload the page. If {{product}} then asks you to sign in, sign in again.",
    NO_SESSION:
      "This browser holds no session that {{product}} can renew. You signed out, the browser deleted what it keeps for {{product}}, or the session reached its time limit: {{idleHours}} hours without use, or {{days}} days since you signed in. Sign in.",
    SESSION_EXPIRED:
      "Your session reached its time limit: {{idleHours}} hours without use, or {{days}} days since you signed in. Sign in again. Anything not yet saved will need to be entered again.",
    SESSION_REVOKED:
      "This session has ended. It was signed out, it was ended when the password was changed, or it reached its time limit. Sign in again. Anything not yet saved will need to be entered again.",
    NOT_A_USER_SESSION:
      "Signing out applies to people, not to API clients. Revoke the API client to stop its access.",
    UNSUPPORTED_MEDIA_TYPE:
      "The request was not sent in the form {{product}} expects, so {{product}} did not act on it. This is a fault in the screen or the integration, not in anything you did. Reload the page and try again. If it happens again, contact support and give the reference {{requestId}}.",
    SIGNING_KEYS_UNAVAILABLE:
      "{{product}} cannot sign anyone in or check any session right now, because its security keys are not set up correctly on the server. This is a fault on our side, not in anything you did. Wait a few minutes, then try again. If it continues, contact support and give the reference {{requestId}}.",
    VERIFICATION_PEPPER_UNAVAILABLE:
      "{{product}} cannot create or check verification codes right now, because a setting is missing on the server, so your sign-up or verification did not go through. This is a fault on our side. Wait a few minutes, then try again. If it continues, contact support and give the reference {{requestId}}.",
    IDENTITY_ROW_UNREACHABLE:
      "{{product}} could not send a new code for this address because of a fault on our side, so nothing was sent and the codes you already have are unchanged. Wait a few minutes, then try again. If it continues, contact support and give the reference {{requestId}}.",
    INVITATION_NOT_RECORDED:
      "{{product}} could not record the invitation because of a fault on our side, so nobody was invited and no email was sent. Wait a few minutes, then try again. If it continues, contact support and give the reference {{requestId}}.",
    REFRESH_TOKEN_USED:
      "{{product}} stopped while renewing your session, because a safety check in the database refused the change. This is a fault in {{product}}, not in anything you did. Sign in again. If it happens again, contact support and give the reference {{requestId}}.",
    REFRESH_TOKEN_UPDATE_MUST_USE:
      "{{product}} stopped while renewing your session, because a safety check in the database refused the change. This is a fault in {{product}}, not in anything you did. Sign in again. If it happens again, contact support and give the reference {{requestId}}.",
    REFRESH_TOKEN_REWRITE:
      "{{product}} stopped while renewing your session, because a safety check in the database refused the change. This is a fault in {{product}}, not in anything you did. Sign in again. If it happens again, contact support and give the reference {{requestId}}.",
    SESSION_OWNER_MISMATCH:
      "Your new password was not saved, because a safety check refused to end sessions that belong to someone else. This is a fault in {{product}}, not in anything you did. Try the change again. If it happens again, contact support and give the reference {{requestId}}.",
    RATE_LIMIT_UNAVAILABLE:
      "{{product}} cannot count sign-in requests right now, so it is not accepting any for the moment. This is a fault on our side. Wait a minute, then try again. If it continues, contact support and give the reference {{requestId}}.",
    CONFIGURATION_INVALID:
      "{{product}} cannot be reached, because its server did not start. Wait a few minutes, then try again. If it continues, contact support.",
    /**
     * The five upload codes S2-08 added to ERROR_CODES (kernel/files). The
     * words are docs/help/errors.md's, cause then fix, its "Atlas" as
     * {{product}}. FILE_TOO_LARGE's {{limit}} is the body's `limitBytes`,
     * written for people. A code with a "When" column there is a group, one
     * sentence per row.
     */
    FILE_TOO_LARGE:
      "The file is larger than this installation's upload limit of {{limit}}. Upload a smaller file, or compress or split it first.",
    MALWARE_DETECTED:
      "The virus scan found something harmful in this file, so {{product}} did not store it. Do not open the file. Scan your device, and ask the sender for a clean copy, then upload that. If you are sure the file is clean, contact support and give the reference {{requestId}}.",
    ANTIVIRUS_UNAVAILABLE:
      "The virus scanner did not answer, so your file was not stored. {{product}} never stores a file it could not scan. Wait a few minutes, then upload the file again. If it continues, contact support and give the reference {{requestId}}.",
    STORAGE_UNAVAILABLE: {
      uploading:
        "File storage did not accept the file, so the file was not saved. Wait a few minutes, then upload the file again. If it continues, contact support and give the reference {{requestId}}.",
      opening:
        "{{product}} could not create a link to download the file. The file itself is still stored. Wait a few minutes, then open the file again. If it continues, contact support and give the reference {{requestId}}.",
    },
    FEATURE_NOT_CONFIGURED: {
      twoStepSignIn:
        "Two-step sign-in has not been set up on this {{product}} installation, so it cannot be turned on. Contact support. The operator must configure it (Details names the missing setting).",
      uploading:
        "File uploads have not been set up on this {{product}} installation: file storage or virus scanning is missing, so no file is accepted. Contact support. The operator must configure it (Details names the missing settings).",
    },
    /**
     * SN-1 (docs/design/safety-net.md §3.12): a document's date lies in no
     * fiscal year of its company; the field item names the date field.
     */
    DATE_OUTSIDE_FISCAL_CALENDAR:
      "The date is outside every fiscal year of this company, so nothing was saved. Choose a date inside one of its fiscal years, or ask your finance manager to open that year first.",
    /**
     * F7/W1.3 (D338): the entry's period resolves to no fiscal year of the
     * entry's company, so Atlas refuses to number or post it.
     */
    FISCAL_YEAR_NOT_FOUND:
      "The period for this entry does not belong to a fiscal year of the entry's company, so Atlas could not give it an entry number and nothing was posted. Check the entry's date and company. Ask your finance manager to open the fiscal year for that company, then record the entry in a period inside it. If the year is already open, contact support and give the reference {{requestId}}.",
    /**
     * CTL-0 (D209): who may give, change or take away access. One sentence
     * per `meta.reason` (docs/help/errors.md, GRANT_NOT_ALLOWED).
     */
    GRANT_NOT_ALLOWED: {
      self: "Nobody can give themselves a role, add a permission to a role they hold, or change their own role assignment other than ending it now. Ask an owner of the workspace, or another administrator, to make the change.",
      owner:
        "Only an owner of the workspace can give, change or end the Owner role, or suspend or remove an owner. Ask an owner of the workspace to make the change.",
      allAccess:
        "Only an owner of the workspace can give, change or take away all access: add it to a role, remove it, delete a role that holds it, or give, change or end a role that holds it. Ask an owner of the workspace. To give someone wide access, choose the permissions they need instead of all access.",
      lastOwner:
        "This would leave the workspace without an active owner. Give another person the Owner role first, with All scope and no end date, then make this change.",
    },
    /**
     * CTL-1 (D291, RR-1): the built-in roles are protected. Deleting one, or
     * removing all access ('*') from the built-in Owner role, would leave the
     * workspace with nobody able to administer it.
     */
    SYSTEM_ROLE_PROTECTED:
      "{{product}} protects its built-in roles: they cannot be deleted, and the Owner role cannot lose all access. If ownership is changing, give another person the Owner role first, with All scope and no end date. Otherwise, remove people from the role instead of deleting it.",
    /**
     * D249 (RR-1c, N15): the claimant is the person the claim's Employee is
     * linked to OR the person who filed the claim, even when they hold
     * expense.approve through the Owner role.
     */
    SELF_APPROVAL_BLOCKED:
      "An expense claim cannot be approved by the person claiming it — the employee it is for, or the person who filed it — even when they can approve other people's claims. Ask a different approver to review this claim.",
    /**
     * W2.1d (migration 0070): the provisioning seeder ran without the
     * workspace it was told to seed. Provisioning is a background step, so a
     * person meets this only as a workspace that did not finish setting up.
     */
    TENANT_CONTEXT_REQUIRED:
      "{{product}} could not finish setting up this workspace, because a safety check in the database refused to create its roles outside the workspace itself. This is a fault in {{product}}, not in anything you did. Try again in a few minutes. If it continues, contact support and give the reference {{requestId}}.",
    /**
     * W2.1d (RR-3a, slice 3): filing an expense claim with no employeeId for
     * a sign-in HR has not linked to an Employee. Nothing is written.
     */
    EMPLOYEE_NOT_LINKED:
      "Your sign-in is not linked to an employee record, so {{product}} has nobody to file this expense claim for. Ask HR to link your sign-in to your employee record, then file the claim again.",
    /**
     * FIX-STATUS: an edit named a status that only an action sets (a pay
     * run's Posted, a budget's Approved or Locked). Nothing was saved.
     */
    STATUS_REQUIRES_ACTION:
      "This status cannot be set by editing the record, because it has accounting or approval meaning. Use the record's own action instead, such as Post for a pay run. Where there is no such action, the status cannot be set yet. Nothing was saved.",
    MFA_ALREADY_ENROLLED:
      "Two-step sign-in is already set up on this account, and your current authenticator and recovery codes still work. To change phones, turn two-step sign-in off with a current or recovery code, then set it up again.",
    /**
     * W2.8b: a second factor is required here (a privileged role, or the
     * workspace's rule) and none is set up. Also the explanation on the
     * set-up step that sign-in and workspace creation lead to.
     */
    MFA_REQUIRED:
      "Your role in this workspace, or the workspace's own rule, needs two-step sign-in, and your account has none set up yet. Set it up now with an authenticator app: the workspace opens as soon as it is confirmed. If you were already signed in, sign in again to set it up.",
    /**
     * W2.8b-2: resetting another member's two-step sign-in (an owner's
     * action; no screen yet, D1126). Each says that nothing was changed.
     */
    MFA_RESET_OWNER_REQUIRED:
      "Only an owner of this workspace can reset another member's two-step sign-in. Ask an owner; if the workspace has no other owner, contact {{product}} support. Nothing was changed.",
    MFA_RESET_OWN_FACTOR:
      "You cannot reset your own two-step sign-in here. Turn it off in your own security settings with a current or recovery code; without either, ask another owner or {{product}} support. Nothing was changed.",
    MFA_RESET_NEEDS_SUPPORT:
      "This person also belongs to a workspace you do not own, so you cannot reset their two-step sign-in. {{product}} support can reset it after checking their identity. Nothing was changed.",
    MFA_NOT_SET_UP:
      "This person has no two-step sign-in to reset. Nothing was changed and no email was sent.",
    /**
     * D983: turning on two-step sign-in while signed in needs the current
     * password, and the setup must be finished within 10 minutes on the
     * same session. Also the words of the setup's password step.
     */
    REAUTHENTICATION_REQUIRED:
      "Enter your current password to set up two-step sign-in. {{product}} asks for it so that nobody using a session you left open can turn it on with their own phone. If you are asked again, the setup took longer than 10 minutes or was started somewhere else: enter your password and start again. Nothing has changed yet.",
    /**
     * D451: posting a pay run, or another posting, found no usable account
     * for a purpose, not even the suspense account. Nothing was written.
     */
    ACCOUNT_ROLE_NOT_MAPPED:
      "{{product}} found no usable account to post to for one of its purposes in this legal entity, not even the suspense account. An administrator must add or reactivate the account in the chart of accounts; there is no screen to change account mapping yet. Nothing was posted.",
    /** D451: a payroll purpose is mapped to an account of the wrong kind. */
    ACCOUNT_ROLE_WRONG_TYPE:
      "One of the payroll purposes is mapped to the wrong kind of account, for example net pay to an expense account. Map it to an account of the right kind, then post again. Nothing was posted.",
    /** D451: the pay run names no employee, or every amount is zero. */
    PAY_RUN_EMPTY:
      "This pay run has nothing to post: it names no employee, or every amount is zero. Enter the amounts, then post again. Nothing was posted.",
    /** D451: an employee's deduction total and deduction lines differ. */
    DEDUCTIONS_MISMATCH:
      "For at least one employee, the deduction total and the deduction lines do not agree. Send one of them, or make them agree, then post again. Nothing was posted.",
    /** D451: the pay run names an employee this workspace does not have. */
    EMPLOYEE_NOT_FOUND:
      "The pay run names an employee this workspace does not have. Check the employees on the pay run, then post again. Nothing was posted.",
    /** D451: an employee names a department that does not exist. */
    EMPLOYEE_DEPARTMENT_NOT_FOUND:
      "An employee on the pay run belongs to a department that no longer exists. Correct the employee's department, then post again. Nothing was posted.",
    /** D451: employees of another company, or of two companies, in one run. */
    EMPLOYEE_COMPANY_MISMATCH:
      "The pay run includes employees of another company. One pay run carries one company's payroll: split the run, or correct the employees' departments. Nothing was posted.",
    /** D451: no company named and more than one could own the run. */
    PAY_RUN_COMPANY_REQUIRED:
      "The pay run does not say which company it belongs to, and {{product}} cannot tell from its employees. Create the pay run with its company, then post it. Nothing was posted.",
    /** D451: deductions above gross pay for an employee. */
    NET_PAY_NEGATIVE:
      "For at least one employee, deductions are greater than gross pay, which would make net pay negative. Reduce the deductions, an advance recovery included, then post again. Nothing was posted.",
    /**
     * MF-B0 (migration 0074): a bill of materials, a line, a production order
     * or a material would make completion book a product's stock to the
     * stock account its inventory role does not name. Nothing was saved
     * past the refused row.
     */
    INVENTORY_ROLE_CONFLICT:
      "This product's inventory role, set by a person or fixed by stock already booked, is a stock account that production would contradict: it would use the other one. Use another product, or, before any stock is booked, set the product's inventory role to the one production uses, then try again.",
    STOCK_OWNERSHIP_VIOLATION:
      "The stock movement conflicts with who owns the stock: it gives a customer's goods a value, or revalues stock the warehouse does not hold. Move a customer's goods at value zero, or revalue only stock the warehouse holds, then try again.",
    /**
     * REP-CCY (CUR-1 single-book rule): a report asked for the whole
     * workspace, whose legal entities keep their books in different
     * currencies. Nothing was added up or converted.
     */
    LEGAL_ENTITY_REQUIRED:
      "This workspace keeps its books in more than one currency, so a report shows one legal entity's books at a time, in that entity's currency. Choose the legal entity. Nothing was added up across currencies.",
    /**
     * PAY-ALLOC (docs/design/payment-allocation.md §4.1): an allocation that
     * is invalid data. Overpaying is not one: the excess stays on account.
     */
    ALLOCATION_PARTY_MISMATCH:
      "This invoice belongs to another customer or supplier, so this payment cannot settle it. Choose an invoice of the party who made or received the payment. Nothing was saved.",
    ALLOCATION_DIRECTION_MISMATCH:
      "A customer receipt settles sales invoices, and a supplier payment settles supplier bills. Choose a document of the right kind. Nothing was saved.",
    ALLOCATION_LEGAL_ENTITY_MISMATCH:
      "The payment and the invoice are in the books of different legal entities. Allocate the payment to an invoice of its own legal entity. Nothing was saved.",
    CROSS_CURRENCY_ALLOCATION_NOT_SUPPORTED:
      "The payment and the invoice are in different currencies, and {{product}} cannot settle one currency with another yet. Choose an invoice in the payment's currency. Nothing was saved.",
    ALLOCATION_EXCEEDS_AMOUNT_DUE:
      "This is more than the invoice still owes. Allocate no more than the amount due; the rest of the payment stays on account for another invoice. Nothing was saved.",
    ALLOCATION_EXCEEDS_UNALLOCATED:
      "Together these amounts are more than the payment has left to allocate. Reduce them, or use another payment for the rest. Nothing was saved.",
    ALLOCATION_DATE_BEFORE_DOCUMENT:
      "The date is before the payment's or the invoice's date. Choose a later date, or leave it empty to use the later of the two. Nothing was saved.",
    AMOUNT_PRECISION_EXCEEDED:
      "The amount has more decimal places than the currency allows, and {{product}} never rounds it for you. Enter it again with fewer decimal places.",
    INVOICE_WRITTEN_OFF:
      "This invoice has been written off, so a payment cannot be allocated to it. Record money recovered on it as a recovery instead.",
    ACCOUNT_ROLE_TYPE_MISMATCH:
      "The account chosen for this purpose is of the wrong type, for example an asset account for money customers paid in advance. Choose an account of the type the purpose needs.",
    SETTLE_THROUGH_PAYMENT:
      "A supplier bill is paid by recording a supplier payment and allocating it to the bill, not by marking it paid. Nothing was changed.",
    /**
     * D725: a person's sign-in email and name are their own {{product}}
     * account, shared by every workspace they belong to. Nothing changed.
     */
    IDENTITY_SELF_ONLY:
      "A person's sign-in email and name belong to their own {{product}} account, which every workspace they belong to shares, so only they can change them, from their own profile. Nothing was changed.",
    /** D725: the new sign-in email is already another account's. */
    EMAIL_ADDRESS_IN_USE:
      "Another {{product}} account already signs in with that email address. Nothing was changed, and your current address keeps working. Choose a different address.",
    /**
     * D709: a platform.* permission (creating a sign-in account) belongs to
     * {{product}}'s operators, never to a workspace role or an API client.
     */
    PLATFORM_OPERATOR_REQUIRED:
      "This action is reserved for {{product}} platform operators: no workspace role, all access included, can perform it. To add someone to your workspace, ask them to sign up with their own email address, then invite them. Nothing was changed.",
    /**
     * TAX-P0 (docs/design/tax-p0.md §3.4; docs/help/errors.md E2): refusals
     * of document data that cannot be true. Missing tax set-up never
     * refuses: it is a notice (TAX_NOTICE_CODES), not an error.
     */
    DERIVED_AMOUNT_MISMATCH:
      "The {{field}} you entered, {{received}}, is not the {{expected}} that {{product}} calculates from the lines and their tax codes. Leave the field empty and it is filled in for you, or correct the lines so they give the figure you expect.",
    TAX_CODE_NOT_APPLICABLE: {
      NotEffective:
        "The tax code on line {{line}} is not in force on the document's date ({{date}}). Choose the version of the code in force on that date, or leave the code empty to use the default.",
      OtherEntity:
        "The tax code on line {{line}} belongs to another company's legal entity. Choose a tax code of this document's company.",
      Inactive:
        "The tax code on line {{line}} has been deactivated. Choose an active tax code.",
      NotSalesTax:
        "The code on line {{line}} is a withholding or other tax, not VAT or sales tax. Choose a VAT or sales tax code for the line.",
      NotFound:
        "Line {{line}} names a tax code this workspace does not have. Choose one of the workspace's tax codes.",
    },
    STATED_TAX_OUT_OF_TOLERANCE:
      "The supplier's tax on line {{line}}, {{received}}, does not match {{taxCode}} ({{ratePercent}}%), which gives {{expected}}. Check the supplier's invoice. If the supplier charged another rate, choose that rate's code for the line; if they charged no tax, set the line to \"no tax charged\".",
    TAX_RECOMPUTE_MISMATCH:
      "A tax code on this document changed after it was saved, so its figures are no longer the ones {{product}} would post. Nothing was posted. Open the document and save it again to take the current figures, then post it. An approved invoice whose amounts change goes back to draft to be approved again.",
    TAX_CODE_IN_USE:
      "Posted documents use this version of the tax code, so its {{field}} cannot change. To change the rate from a date, add a new version of the code with a later start date, and give this version an end date.",
    DOCUMENT_HAS_NO_LINES:
      "This document has no lines, so there is nothing to post. Add the lines it bills, then post it.",
    UNKNOWN_CURRENCY:
      "{{currencyCode}} is not a currency {{product}} can keep amounts in. Choose the currency the document is really in.",
    DOCUMENT_TOTALS_MISMATCH:
      "{{product}} stopped the posting because the document's totals did not add up, and nothing was posted. Contact support and give the reference {{requestId}}.",
    COSTING_METHOD_LOCKED:
      "This item's costing method was fixed when its stock first moved, and it cannot change: stock already valued under one method cannot be valued under another. Keep the method, or create a new item for the other method.",
    COSTING_METHOD_NOT_ALLOWED:
      "The costing method is not one the legal entity's country pack allows. Choose a method from the pack's list, shown in the costing settings, then try again.",
    /**
     * NUM-P0 (docs/design/numbering-p0.md §3.3; docs/help/errors.md E3):
     * a number series change that would break its rules. A missing series
     * never refuses: it is a notice (NUMBERING_NOTICE_CODES).
     */
    NUMBER_SERIES_PATTERN_INVALID:
      "This pattern cannot number documents ({{reason}}). Start with a letter, use only Latin letters, digits and - / _ ., add one counter such as {######}, and use {FY} only for a series that restarts each fiscal year.",
    NUMBER_SERIES_IN_USE:
      "This number series has already issued numbers, so its reset cannot change. Keep the reset, and change the pattern instead if the numbers should look different.",
    NUMBER_SERIES_PATTERN_IN_USE:
      "The pattern's prefix overlaps the {{documentKind}} series, so their numbers could look alike. Choose a prefix that neither starts nor is started by another series' prefix.",
    NUMBER_SERIES_WOULD_COLLIDE:
      "With this pattern the series would issue {{example}} again, and that number is already in use. Choose another prefix or counter length, so no issued number can repeat.",
    /**
     * The caller's scope does not reach what was asked. REP-BRANCH (D682):
     * a ledger report refused to a caller limited to part of the
     * organisation names its reason in `meta.reason`; each reason has its
     * own sentence. No figure was shown.
     */
    SCOPE_DENIED: {
      default:
        "Your access does not reach this. Ask your administrator for a role whose scope covers it.",
      LINES_CARRY_NO_BRANCH:
        "Your access to this legal entity's books is limited to some branches, and the ledger lines this report adds up do not all record a branch yet. A figure for your branches would leave out their own sales and costs, so none is shown. Someone with access to every company of the legal entity can run this report.",
      LINES_CARRY_NO_COMPANY:
        "Your company shares its legal entity with other companies, and the ledger lines this report adds up do not all record a company or a branch, so your company's lines cannot be told apart from the others'. None is shown. Someone with access to every company of the legal entity can run this report.",
      BRANCH_LINES_DO_NOT_BALANCE:
        "Your part of this legal entity's books does not balance on its own, because an entry moves an amount between your part and the rest. No trial balance or balance sheet is shown for it. Its profit or loss can still be shown.",
      SCOPE_KIND_NOT_IN_LEDGER:
        "Your access is limited to a department or to your own records, and the ledger does not record either, so no figure is shown. Someone with branch, company or workspace-wide access can run this report.",
      LEGAL_ENTITY_NOT_IN_SCOPE:
        "Your access covers no branch and no company of the legal entity this report was asked for, so nothing in its books is yours to see, and no figure is shown. Choose a legal entity your access covers, or ask someone with access to this one to run the report.",
    },
  },
  setup: {
    center: {
      title: "Setup Center",
      subtitle:
        "Guided implementation from blank environment to production-ready.",
      continue: "Continue setup",
      score: "Readiness score: {{score}}",
      blockingErrors_one: "{{count}} blocking error",
      blockingErrors_other: "{{count}} blocking errors",
      warnings_one: "{{count}} warning",
      warnings_other: "{{count}} warnings",
      categories: "Setup categories",
      blockingItems: "Blocking items",
      auditLog: "Audit log",
      noBlockingItems: "No blocking items.",
    },
    /** GET /v1/health/tenant `readinessSegments[].phase`. */
    phases: {
      TaxConfiguration: "Tax configuration",
      ApprovalWorkflows: "Approval workflows",
      OpeningBalances: "Opening balances",
    },
    /**
     * The API sends these as English sentences, not codes
     * (tenant-health.controller.ts). Matched by exact text; anything else is
     * shown as sent.
     */
    messages: {
      noTaxCodes: "No tax code is active in this workspace.",
      noApprovalMatrix:
        "No approval matrix is active, so business documents cannot be approved.",
      noPostableAccounts: "The chart of accounts has no postable accounts.",
      openingBalancesNeedAccounts:
        "Opening balances cannot be entered until accounts exist.",
    },
  },
  /** HomeDashboardPage (GET /v1/dashboard/home). */
  home: {
    title: "Home",
    readiness: "Readiness score {{score}}",
    blockingItems_one: "{{count}} blocking item",
    blockingItems_other: "{{count}} blocking items",
    newSalesOrder: "New Sales Order",
    continueSetup: "Continue Setup",
    pendingApprovals: "Pending approvals",
    recentRecords: "Recent records",
    columns: {
      type: "Type",
      record: "Record",
      submittedBy: "Submitted by",
      date: "Date",
      number: "Number",
      status: "Status",
      total: "Total",
    },
    /**
     * `kpis[].label` and `.meta`. The API sends these as English text with
     * no code (dashboard.service.ts), so they are matched by exact text
     * (features/shell/home-text.ts); anything else is shown as sent.
     */
    kpis: {
      revenue: "Revenue ({{days}} days)",
      revenueMeta: "Posted, sent and paid invoices",
      receivables: "Receivables outstanding",
      nothingOverdue: "Nothing overdue",
      overdue_one: "{{count}} overdue",
      overdue_other: "{{count}} overdue",
      commitments: "Purchase commitments",
      commitmentsMeta: "Approved and in-flight purchase orders",
      openSalesOrders: "Open sales orders",
      openSalesOrdersMeta: "Not yet invoiced or cancelled",
    },
    /** `alerts[]`, by their code. */
    alerts: {
      provisioning: "Your workspace is still being set up.",
      overdueInvoices_one: "{{count}} overdue invoice.",
      overdueInvoices_other: "{{count}} overdue invoices.",
      approvalsPending_one: "{{count}} item waiting for approval.",
      approvalsPending_other: "{{count}} items waiting for approval.",
    },
    /** Alert code SETUP_<TABLE>_MISSING, by table. */
    setup: {
      COMPANY: "Create a company",
      ACCOUNT: "Load a chart of accounts",
      FISCALYEAR: "Open a fiscal year",
      TAXCODE: "Define tax codes",
      BRANCH: "Add a branch or location",
      WAREHOUSE: "Add a warehouse",
      CUSTOMER: "Add a customer",
      SUPPLIER: "Add a supplier",
      PRODUCT: "Add a product or service",
    },
  },
  /** ProfilePage. */
  profile: {
    title: "Profile & preferences",
    subtitle: "Personal information, locale, timezone, and security.",
    card: "Profile",
    displayName: "Display name",
    email: "Email",
    locale: "Locale",
    localeHint:
      "Your language (English or Arabic) and how numbers, dates and amounts are written.",
    /** No locale saved: the interface's language is the device's, or English. */
    notChosen: "Not chosen ({{language}})",
    timezone: "Timezone",
    timezoneHint: "Every date and time in the product is shown in this zone.",
    security: "Security",
    mfa: "Multi-factor authentication",
    /**
     * The sign-in rule GET /v1/users/me reports (mfaPolicy.requiredFor), never
     * a plan claim: it said "Required for Professional and above." with no
     * plan tiers and nothing enforced (D1044). Since W2.8b the rule is
     * enforced at every sign-in, and privileged means owners, administrators
     * and finance approvers (the catalog's mfa_privileged grants); nobody is
     * only what a deployment that turned enforcement off reports.
     */
    mfaRule: {
      nobody:
        "Optional. Recommended for owners, administrators and finance approvers.",
      privileged:
        "Required for owners, administrators and finance approvers of this workspace.",
      everyone: "Required by this workspace for every member.",
      unknown: "The sign-in rule could not be read.",
    },
    mfaOn: "Enabled",
    mfaOff: "Not enrolled",
    manageMfa: "Manage MFA",
    enrollMfa: "Enroll MFA",
    sessions: "Active sessions",
    device: "Device",
    /** Read from the session's user-agent string; the raw string is the tooltip (D1045). */
    deviceOn: "{{browser}} on {{os}}",
    deviceUnknownBrowser: "Unknown browser on {{os}}",
    deviceUnknown: "Unknown device",
    ip: "IP",
    lastActive: "Last active",
    current: "current",
    revoke: "Revoke",
  },
  /** InboxPage (GET /v1/approvals). */
  inbox: {
    title: "Inbox",
    subtitle: "Approvals, assignments, and SLA escalations.",
    tabs: {
      actionRequired: "Action Required",
      waitingOnOthers: "Waiting on Others",
      updates: "Updates",
    },
    pendingTasks_one: "{{count}} pending task",
    pendingTasks_other: "{{count}} pending tasks",
    columns: {
      task: "Task",
      type: "Type",
      requestedBy: "Requested by",
      received: "Received",
      slaDue: "SLA due",
      risk: "Risk",
      actions: "Actions",
    },
    risk: {
      Low: "Low",
      Medium: "Medium",
      High: "High",
    },
    approve: "Approve",
    reject: "Reject",
  },
  /** NotificationsPage (GET /v1/notifications). */
  notifications: {
    title: "Notifications",
    unread_one: "{{count}} unread",
    unread_other: "{{count}} unread",
    markAllRead: "Mark all read",
    tabs: {
      all: "All",
      unread: "Unread",
    },
    new: "New",
    columns: {
      type: "Type",
      message: "Message",
      received: "Received",
      actions: "Actions",
    },
    markRead: "Mark read",
  },
  /** SearchResultsPage (GET /v1/search). */
  search: {
    title: "Global search",
    subtitle:
      "Search customers, suppliers, products, orders, invoices, documents, and audit events.",
    placeholder: "Search… (e.g. 'gold invoice')",
    module: "Module",
    allModules: "All modules",
    emptyTitle: "Type to search",
    emptyMessage:
      "Results respect your permissions — you only see records you can access.",
    results_one: "{{count}} result for “{{query}}”",
    results_other: "{{count}} results for “{{query}}”",
    noResults: "No results",
    noResultsHint: "Try different keywords or filters.",
  },
  /** Sidebar and command palette. Key = navKey(label) (lib/i18n/nav.ts). */
  nav: {
    home: "Home",
    inbox: "Inbox",
    organization: "Organization",
    structure: "Structure",
    legalEntities: "Legal Entities",
    companies: "Companies",
    branches: "Branches",
    departments: "Departments",
    locations: "Locations",
    crm: "CRM",
    overview: "Overview",
    leads: "Leads",
    opportunities: "Opportunities",
    contacts: "Contacts",
    activities: "Activities",
    campaigns: "Campaigns",
    planning: "Planning",
    demandForecasts: "Demand Forecasts",
    mrpRuns: "MRP Runs",
    eCommerce: "E-commerce",
    storefronts: "Storefronts",
    productFeeds: "Product Feeds",
    onlineOrders: "Online Orders",
    compliance: "Compliance",
    calendar: "Calendar",
    regulatoryReports: "Regulatory Reports",
    eInvoicing: "E-invoicing",
    auditFiles: "Audit Files",
    dataSubjectRequests: "Data Subject Requests",
    retentionPolicies: "Retention Policies",
    configuration: "Configuration",
    governance: "Governance",
    segregationOfDuties: "Segregation of Duties",
    pendingChanges: "Pending Changes",
    activatedVersions: "Activated Versions",
    entityBuilder: "Entity Builder",
    formViewDesigner: "Form & View Designer",
    finance: "Finance",
    financialDashboard: "Financial Dashboard",
    chartOfAccounts: "Chart of Accounts",
    journalEntries: "Journal Entries",
    generalLedger: "General Ledger",
    trialBalance: "Trial Balance",
    financialStatements: "Financial Statements",
    customerInvoices: "Customer Invoices",
    supplierInvoices: "Supplier Invoices",
    payments: "Payments",
    bankReconciliation: "Bank Reconciliation",
    cashManagement: "Cash Management",
    fixedAssets: "Fixed Assets",
    taxDashboard: "Tax Dashboard",
    periodClose: "Period Close",
    exchangeRates: "Exchange Rates",
    budgeting: "Budgeting",
    consolidation: "Consolidation",
    sales: "Sales",
    quotations: "Quotations",
    salesOrders: "Sales Orders",
    customers: "Customers",
    deliveryNotes: "Delivery Notes",
    salesInvoices: "Sales Invoices",
    returnsRmas: "Returns / RMAs",
    priceListsDiscounts: "Price Lists & Discounts",
    creditLimitReview: "Credit Limit Review",
    commissions: "Commissions",
    procurement: "Procurement",
    purchaseRequests: "Purchase Requests",
    rfqs: "RFQs",
    purchaseOrders: "Purchase Orders",
    goodsReceipts: "Goods Receipts",
    suppliers: "Suppliers",
    supplierEvaluation: "Supplier Evaluation",
    procurementContracts: "Procurement Contracts",
    inventory: "Inventory",
    products: "Products",
    stockByLocation: "Stock by Location",
    inventoryMovements: "Inventory Movements",
    transfers: "Transfers",
    adjustments: "Adjustments",
    cycleCounts: "Cycle Counts",
    lotSerialTraceability: "Lot / Serial Traceability",
    reorderPlanner: "Reorder Planner",
    valuationReport: "Valuation Report",
    manufacturing: "Manufacturing",
    bomsRoutings: "BOMs / Routings",
    workCenters: "Work Centers",
    productionOrders: "Production Orders",
    mrpPlanner: "MRP Planner",
    qualityInspections: "Quality Inspections",
    hrPayroll: "HR & Payroll",
    employeeDirectory: "Employee Directory",
    leaveRequests: "Leave Requests",
    timeAttendance: "Time & Attendance",
    payrollRuns: "Payroll Runs",
    payslips: "Payslips",
    projects: "Projects",
    serviceTickets: "Service Tickets",
    pos: "POS",
    shipments: "Shipments",
    fleetVehicles: "Fleet Vehicles",
    documents: "Documents",
    reportsBi: "Reports & BI",
    dashboardBuilder: "Dashboard Builder",
    reportBuilder: "Report Builder",
    pivotReportViewer: "Pivot Report Viewer",
    setup: "Setup",
    setupCenter: "Setup Center",
    implementationWizard: "Implementation Wizard",
    auditLogExplorer: "Audit Log Explorer",
    apiWebhookAdmin: "API / Webhook Admin",
    userManagement: "User Management",
    rolesPermissions: "Roles & Permissions",
    workflowBuilder: "Workflow Builder",
    approvalMatrixDesigner: "Approval Matrix Designer",
    countryPackManager: "Country Pack Manager",
    industryPackManager: "Industry Pack Manager",
    integrationCenter: "Integration Center",
    dataImportCenter: "Data Import Center",
    tenantSettings: "Tenant Settings",
    securityCenter: "Security Center",
    billingSubscription: "Billing / Subscription",
    marketplace: "Marketplace",
    notifications: "Notifications",
    search: "Search",
    profile: "Profile",
  },
  ...screensEn,
  ...controlsEn,
} as const;
